import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Spinner, Badge, Button, Modal, Form, Dropdown } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { FaUserShield, FaUser, FaUserCog, FaPlus, FaTrash } from "react-icons/fa";
import { FiSearch, FiRefreshCw, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const fetchingRef = useRef(false);

    const [newUser, setNewUser] = useState({
        user_name: "",
        password: "",
        user_role: "editor",
        status: "active",
        can_access: 0,
    });

    const [editUserModal, setEditUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editingName, setEditingName] = useState("");

    const userRole = localStorage.getItem("user_role");

    const fetchUsers = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;

        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/admin/getallusers", { withCredentials: true });
            setUsers(res.data);
        } catch (err) {
            toast.error("Failed to fetch users");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const getSingaporeTimeUTC = () => {
        return moment().tz('Asia/Singapore').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...newUser,
                created_date: getSingaporeTimeUTC(),
                updated_date: getSingaporeTimeUTC(),
            };
            await axios.post("https://www.azzenterprises.com/api/admin/adduser", payload, { withCredentials: true });
            toast.success("User added successfully!");
            setShowAddModal(false);
            setNewUser({ user_name: "", password: "", user_role: "editor", status: "active", can_access: 0 });
            fetchUsers();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to add user";
            toast.error(message);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        try {
            await axios.delete(`https://www.azzenterprises.com/api/admin/deleteuser/${selectedUser.id}`, { withCredentials: true });
            toast.success("User deleted successfully!");
            setConfirmDeleteModal(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to delete user";
            toast.error(message);
        }
    };

    const handleUpdateUser = async (id, data) => {
        try {
            const payload = { ...data, updated_date: getSingaporeTimeUTC() };
            await axios.put(`https://www.azzenterprises.com/api/admin/updateuser/${id}`, payload, { withCredentials: true });

            setUsers((prev) =>
                prev.map((u) => (u.id === id ? { ...u, ...data } : u))
            );

            const editedFields = Object.keys(data).map((key) => {
                let value = data[key];
                switch (key) {
                    case "status": return `Status ${value} updated successfully`;
                    case "user_role": return `Role ${value} updated successfully`;
                    case "user_name": return `User Name ${value} updated successfully`;
                    case "can_access": return `Access ${value === 1 ? "to view all customers" : "to view only assigned customers"}`;
                    default: return `${key} (${value})`;
                }
            });

            toast.info(`${editedFields.join(", ")}`);
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to update user";
            toast.error(message);
        }
    };

    const formatDate = (date) => {
        return moment(date)
            .tz("Asia/Singapore")
            .format("DD/MM/YYYY hh:mm:ss A");
    };
    console.log(formatDate("2025-09-28T21:47:15.000Z"))

    const getRoleBadge = (role) => {
        switch (role) {
            case "superadmin": return <Badge bg="danger" className="d-flex align-items-center gap-1"><FaUserShield /> Super Admin</Badge>;
            case "admin": return <Badge bg="primary" className="d-flex align-items-center gap-1"><FaUserCog /> Admin</Badge>;
            case "supervisor": return <Badge bg="info" className="d-flex align-items-center gap-1"><FaUser /> Supervisor</Badge>;
            case "editor": return <Badge bg="warning" className="d-flex align-items-center gap-1"><FaUser /> Editor</Badge>;
            case "viewer": return <Badge bg="secondary" className="d-flex align-items-center gap-1"><FaUser /> Viewer</Badge>;
            default: return <Badge bg="dark" className="d-flex align-items-center gap-1"><FaUser /> User</Badge>;
        }
    };

    const hasInactiveUsers = users.some((user) => user.status === "inactive");

    const handleEditUserName = (user) => {
        setEditingUser(user);
        setEditingName(user.user_name);
        setEditUserModal(true);
    };

    const columns = [
        { name: "#", selector: (row, index) => index + 1, width: "60px" },
        {
            name: "User Name",
            selector: (row) => row.user_name,
            sortable: true,
            cell: (row) => (
                <span
                    style={{ cursor: "pointer", color: "#0d6efd", textDecoration: "underline" }}
                    onClick={() => handleEditUserName(row)}
                >
                    {row.user_name}
                </span>
            ),
            grow: 1,
        },
        { name: "Created Date", selector: (row) => formatDate(row.created_date), sortable: true },
        { name: "Updated Date", selector: (row) => formatDate(row.updated_date), sortable: true },
        {
            name: "Role",
            selector: (row) => row.user_role,
            sortable: true,
            cell: (row) => (
                <Dropdown>
                    <Dropdown.Toggle variant="link" size="sm" className="p-0 text-decoration-none" style={{ color: "inherit" }}>
                        {getRoleBadge(row.user_role)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {["user", "viewer", "editor", "supervisor", "admin"].map((role) => (
                            <Dropdown.Item key={role} active={row.user_role === role} onClick={() => handleUpdateUser(row.id, { user_role: role })}>
                                {getRoleBadge(role)}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            ),
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => {
                const isActive = row.status === "active";
                return (
                    <div className="d-flex align-items-center gap-3">
                        <Badge bg={isActive ? "success" : "warning"}>
                            {isActive ? "Active" : "Inactive"}
                        </Badge>
                        <span
                            role="button"
                            onClick={() => handleUpdateUser(row.id, { status: isActive ? "inactive" : "active" })}
                            style={{ cursor: "pointer", fontSize: "1.5rem" }}
                        >
                            {isActive ? <FiToggleRight className="text-success" /> : <FiToggleLeft className="text-warning" />}
                        </span>
                    </div>
                );
            },
        },
        {
            name: "Access",
            selector: (row) => row.can_access,
            sortable: true,
            cell: (row) => {
                const canAccess = row.can_access === 1;
                return (
                    <div className="d-flex align-items-center gap-3">
                        <Badge bg={canAccess ? "success" : "danger"}>
                            {canAccess ? "Enabled" : "Disabled"}
                        </Badge>
                        <span
                            role="button"
                            onClick={() => handleUpdateUser(row.id, { can_access: canAccess ? 0 : 1 })}
                            style={{ cursor: "pointer", fontSize: "1.5rem" }}
                        >
                            {canAccess ? <FiToggleRight className="text-success" /> : <FiToggleLeft className="text-danger" />}
                        </span>
                    </div>
                );
            },
        },
        ...(hasInactiveUsers
            ? [
                {
                    name: "Actions",
                    cell: (row) =>
                        row.status === "inactive" ? (
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => { setSelectedUser(row); setConfirmDeleteModal(true); }}
                                title="Delete User"
                            >
                                <FaTrash />
                            </Button>
                        ) : null,
                    ignoreRowClick: true,
                    width: "80px",
                },
            ]
            : []),
    ];

    const filteredUsers = users
        .filter((user) => user.user_role !== "superadmin")
        .filter((user) => user.user_name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                    <div className="input-group" style={{ maxWidth: "300px" }}>
                        <span className="input-group-text"><FiSearch /></span>
                        <input type="text" className="form-control" placeholder="Search by name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={fetchUsers}><FiRefreshCw /> Refresh</Button>
                        {userRole === "superadmin" && <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}><FaPlus /> Add User</Button>}
                    </div>
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center py-5"><Spinner animation="border" /></div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredUsers}
                        pagination
                        highlightOnHover
                        responsive
                        striped
                        persistTableHead
                        fixedHeader
                        fixedHeaderScrollHeight="600px"
                        defaultSortFieldId={1}
                        customStyles={{ headCells: { style: { backgroundColor: "#f8f9fa", fontWeight: "bold" } } }}
                    />
                )}
            </div>

            <Modal show={editUserModal} onHide={() => setEditUserModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Control
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditUserModal(false)}>CANCEL</Button>
                    <Button
                        variant="primary"
                        onClick={async () => {
                            if (!editingUser) return;
                            await handleUpdateUser(editingUser.id, { user_name: editingName });
                            setEditUserModal(false);
                        }}
                    >
                        UPDATE
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Staff</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddUser}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>User Name</Form.Label>
                            <Form.Control type="text" value={newUser.user_name} onChange={(e) => setNewUser({ ...newUser, user_name: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" id="role-dropdown" className="w-100 text-start">{getRoleBadge(newUser.user_role)}</Dropdown.Toggle>
                                <Dropdown.Menu className="w-100">
                                    {["user", "viewer", "editor", "supervisor", "admin"].map((role) => (
                                        <Dropdown.Item key={role} onClick={() => setNewUser({ ...newUser, user_role: role })} active={newUser.user_role === role}>
                                            {getRoleBadge(role)}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" id="status-dropdown" className="w-100 text-start">
                                    {newUser.status === "active" ? <Badge bg="success">Active</Badge> : <Badge bg="warning">Inactive</Badge>}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="w-100">
                                    {["active", "inactive"].map((status) => (
                                        <Dropdown.Item key={status} onClick={() => setNewUser({ ...newUser, status })} active={newUser.status === status}>
                                            {status === "active" ? <Badge bg="success">Active</Badge> : <Badge bg="warning">Inactive</Badge>}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Access</Form.Label>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" id="access-dropdown" className="w-100 text-start">
                                    {newUser.can_access === 1 ? <Badge bg="success">Enabled</Badge> : <Badge bg="danger">Disabled</Badge>}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="w-100">
                                    {[1, 0].map((val) => (
                                        <Dropdown.Item key={val} onClick={() => setNewUser({ ...newUser, can_access: val })} active={newUser.can_access === val}>
                                            {val === 1 ? <Badge bg="success">Enabled</Badge> : <Badge bg="danger">Disabled</Badge>}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>CANCEL</Button>
                        <Button type="submit" variant="primary">ADD</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={confirmDeleteModal} onHide={() => setConfirmDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete user <strong>{selectedUser?.user_name}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmDeleteModal(false)}>No</Button>
                    <Button variant="danger" onClick={handleDeleteUser}>Delete</Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="top-right" autoClose={1200} newestOnTop />
        </div>
    );
};

export default Users;
